import type { PDFDocumentProxy } from 'pdfjs-dist';

export interface PrintContent { element: HTMLElement; dispose: () => void }
export type PreparePrint = (signal: AbortSignal) => Promise<PrintContent>;

function cancelled(signal: AbortSignal) { signal.throwIfAborted(); }

export async function imagePrintContent(url: string, signal: AbortSignal): Promise<PrintContent> {
  const element = document.createElement('div');
  element.className = 'viewer-print-image';
  const image = document.createElement('img');
  image.src = url;
  element.append(image);
  await image.decode();
  cancelled(signal);
  return { element, dispose: () => image.removeAttribute('src') };
}

/** Render every page independently of the visible page/zoom. One scratch canvas
 * is reused; object URLs and rendered images are released when the viewer closes. */
export async function pdfPrintContent(pdf: PDFDocumentProxy, signal: AbortSignal): Promise<PrintContent> {
  const element = document.createElement('div');
  const urls: string[] = [];
  const canvas = document.createElement('canvas');
  const dispose = () => { element.replaceChildren(); urls.forEach(url => URL.revokeObjectURL(url)); canvas.width = canvas.height = 0; };
  try {
    for (let number = 1; number <= pdf.numPages; number++) {
      cancelled(signal);
      const page = await pdf.getPage(number);
      cancelled(signal);
      const original = page.getViewport({ scale: 1 });
      const scale = Math.min(2, Math.sqrt(8_000_000 / (original.width * original.height)));
      const viewport = page.getViewport({ scale });
      canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
      const task = page.render({ canvas, viewport, intent: 'print' });
      const abort = () => task.cancel();
      signal.addEventListener('abort', abort, { once: true });
      try { await task.promise; } finally { signal.removeEventListener('abort', abort); }
      cancelled(signal);
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error('Не удалось подготовить страницу для печати.')), 'image/png'));
      cancelled(signal);
      const url = URL.createObjectURL(blob); urls.push(url);
      const wrapper = document.createElement('div'); wrapper.className = 'viewer-print-page';
      const pageName = `skrepka_document_page_${number}`;
      wrapper.style.page = pageName;
      const style = document.createElement('style');
      style.textContent = `@media print { @page ${pageName} { size: ${original.width / 72 * 25.4}mm ${original.height / 72 * 25.4}mm; margin: 0; } }`;
      const image = document.createElement('img'); image.src = url; image.alt = `Страница ${number}`;
      image.style.width = `${original.width / 72 * 25.4}mm`;
      image.style.height = `${original.height / 72 * 25.4}mm`;
      wrapper.append(style, image); element.append(wrapper);
      await image.decode();
      cancelled(signal);
      // Yield between pages instead of blocking interaction with the viewer.
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    canvas.width = canvas.height = 0;
    return { element, dispose };
  } catch (error) { dispose(); throw error; }
}

export function htmlPrintContent(html: string): PrintContent {
  const source = new DOMParser().parseFromString(html, 'text/html');
  const element = document.createElement('div'); element.className = 'viewer-print-document';
  // HTML was sanitized by the document reader; keep document styles confined
  // to a shadow root so they cannot alter the application's screen styles.
  const shadow = element.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = ':host{display:block;color:#000;background:#fff} .docx-wrapper{background:white!important;padding:0!important} section.docx{box-shadow:none!important;margin:0!important;max-width:100%!important;break-after:page} section.docx:last-child{break-after:auto}';
  shadow.append(...Array.from(source.querySelectorAll('head style')), ...Array.from(source.body.childNodes), style);
  return { element, dispose: () => element.remove() };
}
